import { IObservableValue, observable, runInAction } from 'mobx';
import type { CSSObject } from 'tss-react/types';

import { mediaQueryMatch } from '@egr/xbox/utils/UserAgent';
import { getEnvVar } from '@egr/xbox/utils/ReactScriptHelper';
import { appInfo } from '@egr/xbox/app-api/AppInfo';

import { isAndroidApp } from '@egr/xbox/app-api/Communication';
import { isNotNullOrEmpty } from '@egr/wcf/modules/utils/string';
import { isNullOrZero } from '@egr/xbox/utils/Number';
import { parseValidInt } from '@egr/xbox/utils/String';

/* iphone X */
const MediaQueryIphoneX: string = '@media only screen and (device-width: 375px) and (device-height: 812px) and (-webkit-device-pixel-ratio: 3)';
const MediaQueryIphoneXR: string = '@media only screen and (device-width : 414px) and (device-height : 896px) and (-webkit-device-pixel-ratio : 2)';
const MediaQueryIphoneXS: string = '@media only screen and (device-width : 375px) and (device-height : 812px) and (-webkit-device-pixel-ratio : 3)';
const MediaQueryIphoneXSMax: string = '@media only screen and (device-width : 414px) and (device-height : 896px) and (-webkit-device-pixel-ratio : 3)';

export function iPhoneXQueries(cssProperties: CSSObject): CSSObject {
    return {
        [MediaQueryIphoneX]: cssProperties,
        [MediaQueryIphoneXR]: cssProperties,
        [MediaQueryIphoneXS]: cssProperties,
        [MediaQueryIphoneXSMax]: cssProperties,
    };
}

export function isIphoneXPortrait(): boolean {
    return (
        mediaQueryMatch(MediaQueryIphoneX.replace('@media ', '') + ' and (orientation: portrait)') ||
        mediaQueryMatch(MediaQueryIphoneXR.replace('@media ', '') + ' and (orientation: portrait)') ||
        mediaQueryMatch(MediaQueryIphoneXS.replace('@media ', '') + ' and (orientation: portrait)') ||
        mediaQueryMatch(MediaQueryIphoneXSMax.replace('@media ', '') + ' and (orientation: portrait)')
    );
}

export const statusBarHeightRef: IObservableValue<number> = observable.box<number>(0);

type SafeAreaDirection = 'top' | 'right' | 'bottom' | 'left';

export const SafeAreaEnvMap: Map<SafeAreaDirection, string> = new Map(
    [
        ['top', isAndroidApp ? (isNullOrZero(appInfo?.status_bar_height) ? '44px' : `${appInfo?.status_bar_height ?? 44}px`) : 'env(safe-area-inset-top)'],
        ['right', 'env(safe-area-inset-right)'],
        ['bottom', 'env(safe-area-inset-bottom)'],
        ['left', 'env(safe-area-inset-left)'],
    ]
);

export const htmlSafeAreaVariables: Record<string, string | undefined> = {
    '--safeareabottom': SafeAreaEnvMap.get('bottom'),
    '--safeareatop': SafeAreaEnvMap.get('top'),
    '--safearealeft': SafeAreaEnvMap.get('left'),
    '--safearearight': SafeAreaEnvMap.get('right'),
};

/**
 * Should be used for Android phones without notch,
 * because there env(safe-inset-area-top) is 0 and not as expected the height of the statusbar
 * @param height
 */
export function setFakeStatusBarHeight(height?: number): void {
    if (screenshotModeDevice != null && height == null) {
        SafeAreaEnvMap.set('top', getFakeStatusbarPadding());
        SafeAreaEnvMap.set('bottom', getFakeBottomPadding());
        return runInAction(() => statusBarHeightRef.set(44));
    }
    if (height != null) {
        SafeAreaEnvMap.set('top', getNumberWithPx(height));
        return runInAction(() => statusBarHeightRef.set(height));
    }
}

export function getStatusbarHeight(): number {
    return parseValidInt(getSafeArea('top'));
}

/**
 * Helper function to add notch-size values
 * @param property
 * @param direction
 */
export function safeArea(
    property: keyof CSSObject,
    direction: SafeAreaDirection,
    defaultValue: number = 0,
): CSSObject {
    const standardValue: string | undefined = SafeAreaEnvMap.get(direction);
    if (standardValue != null) {
        return { [property]: `calc(${defaultValue}px + ${standardValue})` };
    }

    return {[property]: `${defaultValue}px`};
}

/**
 * Only works if safearea... variables were added to styles of HTML
 */
export function getSafeArea(direction: SafeAreaDirection): string {
    let result: string = '';
    switch (direction) {
        case 'bottom':
            result = screenshotModeDevice != null ? getFakeBottomPadding() : getComputedStyle(document.documentElement).getPropertyValue('--safeareabottom');
            break;
        case 'left':
            result = getComputedStyle(document.documentElement).getPropertyValue('--safearealeft');
            break;
        case 'right':
            result = getComputedStyle(document.documentElement).getPropertyValue('--safearearight');
            break;
        case 'top':
            result = screenshotModeDevice != null ? getFakeStatusbarPadding() : getComputedStyle(document.documentElement).getPropertyValue('--safeareatop');
            break;
        default: break;
    }

    if (isNotNullOrEmpty(result)) {
        return result;
    }

    switch (direction) {
        case 'bottom':
            result = getComputedStyle(document.documentElement).getPropertyValue('--safeareaconstantbottom');
            break;
        case 'left':
            result = getComputedStyle(document.documentElement).getPropertyValue('--safeareaconstantleft');
            break;
        case 'right':
            result = getComputedStyle(document.documentElement).getPropertyValue('--safeareaconstantright');
            break;
        case 'top':
            result = getComputedStyle(document.documentElement).getPropertyValue('--safeareaconstanttop');
            break;
        default: break;
    }

    return result;
}

export const WhiteSpacePre: CSSObject = {whiteSpace: 'pre'};

function getFakeStatusbarPadding(): string {
    let statusBarHeight: number = 0;
    switch (screenshotModeDevice) {
        case ScreenshotModeDevice.IPHONE_LG:
            // height given from app should be 44px but is 48px
            statusBarHeight = 48;
            break;
        case ScreenshotModeDevice.IPHONE_SM:
        case ScreenshotModeDevice.IPAD_2G:
        case ScreenshotModeDevice.IPAD_3G:
            statusBarHeight = 20;
            break;
        case ScreenshotModeDevice.ANDROID_PHONE:
            // Pixel 5
            statusBarHeight = 49;
            break;
        default:
            break;
    }
    return `${statusBarHeight}px`;
}

function getNumberWithPx(number: number): string {
    return `${number}px`;
}

function getFakeBottomPadding(): string {
    let statusBarHeight: number = 0;
    switch (screenshotModeDevice) {
        case ScreenshotModeDevice.IPHONE_LG:
            // height given from app should be 44px but is 48px
            statusBarHeight = 48;
            break;
        case ScreenshotModeDevice.IPHONE_SM:
        case ScreenshotModeDevice.IPAD_2G:
        case ScreenshotModeDevice.IPAD_3G:
        case ScreenshotModeDevice.ANDROID_PHONE:
        default:
            break;
    }
    return `${statusBarHeight}px`;
}

export enum ScreenshotModeDevice {
    'IPHONE_LG',
    'IPHONE_SM',
    'IPAD_2G',
    'IPAD_3G',
    'ANDROID_TABLET',
    'ANDROID_PHONE',
    'NO_DEVICE'
}

export const screenshotModeDevice: ScreenshotModeDevice | undefined =
    ScreenshotModeDevice[getEnvVar('SCREENSHOT_MODE_DEVICE')!];